import {
    AppBar, Badge, Button, Divider, Drawer, Hidden, IconButton, InputAdornment, List, ListItem, ListItemIcon, ListItemText, makeStyles, TextField, Theme, ThemeProvider, Toolbar, Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import React, { useEffect, useMemo, useState } from 'react';
import { MdBusiness, MdGroups, MdLogout, MdMenu, MdPerson } from 'react-icons/md';
import { Link as RouterLink, Redirect, Route, RouteComponentProps, Switch, useHistory, useLocation } from 'react-router-dom';
import sysadminRoutes from '../../config/sysadminRoutes';
import { planEatApi } from '../../planEatApi/PlanEatApi';
import { accountsForSelectedCompany, buildAccounts } from './accounts';
import { SysadminDataProvider, useSysadminData } from './SysadminData';
import { SysadminSection, sysadminPaths, SYSADMIN_ROOT } from './sysadminUtils';

const DRAWER_WIDTH = 240;

// The app theme uses CSS color names ('red', 'white', ...) which MUI's color helpers (used by e.g. Alert and Chip) cannot parse.
const hexColor = (main: string) => ({ light: main, main, dark: main, contrastText: '#ffffff' });
const sysadminTheme = (outer: Theme): Theme => ({
    ...outer,
    palette: {
        ...outer.palette,
        primary: { ...outer.palette.primary, contrastText: '#ffffff' },
        secondary: { ...outer.palette.secondary, contrastText: '#ffffff' },
        error: hexColor('#ff0000'),
        warning: hexColor('#ffa500'),
        info: hexColor('#0000ff'),
        success: hexColor('#008000'),
    },
});

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        minHeight: '100vh',
        background: theme.palette.grey[50],
        // Bootstrap's global `legend { float: left }` breaks the notch of MUI outlined inputs
        '& fieldset > legend': {
            float: 'none',
        },
    },
    drawer: {
        [theme.breakpoints.up('md')]: {
            width: DRAWER_WIDTH,
            flexShrink: 0,
        },
    },
    drawerPaper: {
        width: DRAWER_WIDTH,
        background: '#1f2233',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
    },
    brand: {
        padding: theme.spacing(3, 2, 2, 2),
    },
    brandTitle: {
        fontWeight: 700,
    },
    brandSubtitle: {
        opacity: 0.6,
    },
    navItem: {
        borderRadius: 8,
        margin: theme.spacing(0.5, 1),
        width: 'auto',
        color: 'rgba(255,255,255,0.8)',
        '&.Mui-selected, &.Mui-selected:hover': {
            background: theme.palette.primary.main,
            color: 'white',
        },
        '&:hover': {
            background: 'rgba(255,255,255,0.08)',
        },
    },
    navIcon: {
        color: 'inherit',
        minWidth: 40,
        fontSize: 20,
    },
    badge: {
        '& .MuiBadge-badge': {
            position: 'static',
            transform: 'none',
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
        },
    },
    footer: {
        marginTop: 'auto',
        padding: theme.spacing(2),
    },
    divider: {
        background: 'rgba(255,255,255,0.12)',
    },
    userText: {
        opacity: 0.7,
        wordBreak: 'break-all',
        marginBottom: theme.spacing(1),
    },
    logout: {
        color: 'white',
        justifyContent: 'flex-start',
    },
    appBar: {
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
        background: '#1f2233',
    },
    topBar: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: theme.spacing(2),
    },
    companySelector: {
        width: 320,
        maxWidth: '100%',
        background: theme.palette.background.paper,
    },
    content: {
        flexGrow: 1,
        minWidth: 0,
        padding: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
            paddingTop: theme.spacing(10),
        },
    },
}));

const navItems: { section: SysadminSection; label: string; icon: React.ReactNode }[] = [
    { section: 'eaterGroups', label: 'Eater groups', icon: <MdGroups /> },
    { section: 'users', label: 'Users', icon: <MdPerson /> },
    { section: 'companies', label: 'Companies', icon: <MdBusiness /> },
];

// Offices are not in the menu; their pages lead to eater groups
const eaterGroupSections = [sysadminPaths.list('offices').toLowerCase()];

const SidebarContent: React.FC<{ onNavigate: () => void }> = ({ onNavigate }) => {
    const classes = useStyles();
    const location = useLocation();
    const history = useHistory();
    const data = useSysadminData();
    const [currentUser, setCurrentUser] = useState<string>();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            planEatApi.postToken(token).then(response => setCurrentUser(response.data.userId)).catch(() => undefined);
        }
    }, []);

    const counts: Partial<Record<SysadminSection, number>> = useMemo(() => ({
        companies: data.companies.length,
        eaterGroups: data.eaterGroups.filter(g => data.matchesSelectedCompany(data.companyIdOfOffice(g.content.officeId))).length,
        users: accountsForSelectedCompany(data, buildAccounts(data)).length,
    }), [data]);
    const activePath = location.pathname.toLowerCase();

    async function logout() {
        const token = localStorage.getItem('token');
        if (token) {
            await planEatApi.postLogout(token).catch(e => console.log(e));
        }
        localStorage.clear();
        history.push('/');
    }

    return (
        <>
            <div className={classes.brand}>
                <Typography variant="h6" className={classes.brandTitle}>PlanEat</Typography>
                <Typography variant="body2" className={classes.brandSubtitle}>System administration</Typography>
            </div>
            <List>
                {navItems.map(item => {
                    const path = sysadminPaths.list(item.section);
                    const matches = (p: string) => activePath === p || activePath.startsWith(p + '/');
                    const selected = matches(path.toLowerCase()) || (item.section === 'eaterGroups' && eaterGroupSections.some(matches));
                    return (
                        <ListItem key={path} button component={RouterLink} to={path} selected={selected} className={classes.navItem} onClick={onNavigate}>
                            <ListItemIcon className={classes.navIcon}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.label} />
                            {!data.loading && <Badge className={classes.badge} overlap="rectangular" badgeContent={counts[item.section]} max={9999} showZero />}
                        </ListItem>
                    );
                })}
            </List>
            <div className={classes.footer}>
                <Divider className={classes.divider} />
                {currentUser && <Typography variant="caption" component="div" className={classes.userText} style={{ marginTop: 16 }}>Signed in as {currentUser}</Typography>}
                <Button className={classes.logout} startIcon={<MdLogout />} onClick={logout} fullWidth>
                    Log out
                </Button>
            </div>
        </>
    );
};

const ALL = '__all__';

/** Filters offices, eater groups and users by company. */
const CompanySelector: React.FC = () => {
    const classes = useStyles();
    const data = useSysadminData();
    const options = useMemo(() => [
        ALL,
        ...data.companies
            .map(c => c.content)
            .sort((a, b) => (a.companyName ?? '').localeCompare(b.companyName ?? ''))
            .map(c => c.id),
    ], [data.companies]);
    const value = data.selectedCompanyId ?? ALL;
    const label = (id: string) => (id === ALL ? 'All companies' : data.companyById(id)?.companyName || 'Unnamed company');

    // The stored company is only known once the companies are loaded
    if (data.companies.length === 0 && value !== ALL) {
        return <TextField className={classes.companySelector} size="small" variant="outlined" label="Company" value="Loading…" disabled />;
    }

    return (
        <Autocomplete
            className={classes.companySelector}
            size="small"
            disableClearable
            options={options}
            value={value}
            getOptionLabel={label}
            loading={data.loading}
            onChange={(_, value) => data.setSelectedCompanyId(value === ALL ? undefined : value)}
            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    label="Company"
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: <InputAdornment position="start"><MdBusiness /></InputAdornment>,
                    }}
                />
            )}
        />
    );
};

export const SysAdminPage: React.FC = () => {
    const classes = useStyles();
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <ThemeProvider theme={sysadminTheme}>
            <SysadminDataProvider>
                <div className={classes.root}>
                    <AppBar position="fixed" className={classes.appBar} elevation={0}>
                        <Toolbar>
                            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} aria-label="Open menu">
                                <MdMenu />
                            </IconButton>
                            <Typography variant="h6">PlanEat sysadmin</Typography>
                        </Toolbar>
                    </AppBar>
                    <nav className={classes.drawer}>
                        <Hidden mdUp implementation="css">
                            <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} classes={{ paper: classes.drawerPaper }} ModalProps={{ keepMounted: true }}>
                                <SidebarContent onNavigate={() => setMobileOpen(false)} />
                            </Drawer>
                        </Hidden>
                        <Hidden smDown implementation="css">
                            <Drawer variant="permanent" open classes={{ paper: classes.drawerPaper }}>
                                <SidebarContent onNavigate={() => undefined} />
                            </Drawer>
                        </Hidden>
                    </nav>
                    <main className={classes.content}>
                        <div className={classes.topBar}>
                            <CompanySelector />
                        </div>
                        <Switch>
                            <Redirect exact from={SYSADMIN_ROOT} to={sysadminPaths.list('eaterGroups')} />
                            {sysadminRoutes.map(route => (
                                <Route
                                    key={route.path}
                                    path={route.path}
                                    exact={route.exact}
                                    render={(props: RouteComponentProps<any>) => (
                                        // Remount when the path changes so a page never shows state from the previous entity.
                                        // Query changes (e.g. the user side panel) keep the page.
                                        <route.component key={props.location.pathname} name={route.name} {...props} {...route.props} />
                                    )}
                                />
                            ))}
                            <Redirect to={sysadminPaths.list('eaterGroups')} />
                        </Switch>
                    </main>
                </div>
            </SysadminDataProvider>
        </ThemeProvider>
    );
};
